import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  makeStateKey,
  OnInit,
  signal,
  StateKey,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';

import { map, Observable, of, switchMap } from 'rxjs';

import { StateTransferService } from '@fcom/core';
import { Location, LocationSuggestions } from '@fcom/core-api';
import { ConfigService } from '@fcom/core/services/config/config.service';
import { LocationRouteCffService } from '@fcom/core/services/location-routecff/location-routecff.service';
import { SchemaService } from '@fcom/common/services/schema/schema.service';
import { uniqueBy } from '@fcom/core/utils/utils';
import { LocationsService } from '@fcom/locations';
import { LanguageService } from '@fcom/ui-translate';
import {
  OndCrossLinkingType,
  OndLinkTranslations,
  OndMarketingRoutesPath,
  OndPairEnhanced,
} from '@fcom/ond-marketing-landing-core/interfaces/ond-marketing-offers.interface';
import { getCurrentCountry, transformLocationCode } from '@fcom/ond-marketing-landing-core/services/utils';
import { createCityToCityLink } from '@fcom/ond-marketing-landing-core/services/link-utils';
import { OndMarketingSeoService } from '@fcom/ond-marketing-landing-core/services/ond-marketing-seo.service';
import { OndMarketingLinkGroup } from '@fcom/ond-marketing-landing-core/components/ond-marketing-link-list/ond-marketing-link-list.component';

interface SuggestedRouteLink {
  href: string;
  originCityName: string;
  destinationCityName: string;
  disabled?: boolean;
}

interface SuggestedRouteGroup {
  originLocationCode: string;
  originCityName: string;
  links: SuggestedRouteLink[];
}

interface SuggestedRouteContext {
  originCountry: string;
  destinationCountry?: string;
  currentOrigin?: string;
  currentDestination?: string;
}

const hasNavigableLinks = (groups: SuggestedRouteGroup[]): boolean => {
  return groups.some((group) => {
    return group.links.some((link) => {
      return !link.disabled;
    });
  });
};

@Component({
  selector: 'cms-ond-marketing-cross-linking-suggested',
  templateUrl: './cms-ond-marketing-cross-linking-suggested.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CmsOndMarketingCrossLinkingSuggestedComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly locationsService = inject(LocationsService);
  private readonly locationRouteCffService = inject(LocationRouteCffService);
  private readonly languageService = inject(LanguageService);
  private readonly stateTransferService = inject(StateTransferService);
  private readonly schemaService = inject(SchemaService);
  private readonly configService = inject(ConfigService);
  private readonly ondMarketingSeoService = inject(OndMarketingSeoService);

  readonly OndCrossLinkingType = OndCrossLinkingType;

  readonly type = input<OndCrossLinkingType>(OndCrossLinkingType.CITY_TO_CITY);

  private readonly params = toSignal(this.route.params);
  private readonly lang = toSignal(this.languageService.lang);
  private readonly fromTo = this.languageService.translateSignal('ondMarketing.seoUrl.fromTo');
  readonly cityToCityHeading = this.languageService.translateSignal('ondMarketing.heading.cityToCity');

  readonly groups = signal<SuggestedRouteGroup[]>([]);
  readonly loading = signal<boolean>(true);

  readonly mappedGroups = computed<OndMarketingLinkGroup[]>(() => {
    return this.groups().map((group) => ({
      headingArgs: { origin: group.originCityName },
      links: group.links.map((link) => ({
        href: link.href,
        args: { origin: link.originCityName, destination: link.destinationCityName },
        disabled: link.disabled,
      })),
    }));
  });

  readonly hasNavigableLinks = computed<boolean>(() => {
    return hasNavigableLinks(this.groups());
  });

  private static createStateKey(context: SuggestedRouteContext, locale: string): StateKey<LocationSuggestions> {
    return makeStateKey<LocationSuggestions>(
      `c2c-suggested-${context.originCountry}-${context.destinationCountry ?? ''}-${locale}`
    );
  }

  constructor() {
    effect((onCleanup) => {
      const fromTo = this.fromTo();
      const heading = this.cityToCityHeading();
      const type = this.type();
      const lang = this.lang();

      this.loading.set(true);

      /**
       * These i18n values are not immediately available in SSR, so we need to wait for them
       * before we load the API data. We need the values in order to fill in the schema.
       */
      if (!fromTo || !heading || !lang) {
        return;
      }

      const context$ =
        type === OndCrossLinkingType.CITY_TO_CITY_LANDING
          ? of(this.landingContext(lang))
          : this.cityToCityContext$(this.params());

      const subscription = context$
        .pipe(
          switchMap((context) => {
            if (!context) {
              return of<SuggestedRouteGroup[]>([]);
            }

            const locale = this.languageService.localeValue;

            /**
             * Cache the API response so that we don't refetch and rerender the list on the client when in
             * SSR mode.
             */
            return this.locationRouteCffService
              .withLocationSuggestionsFallback(
                this.stateTransferService.wrapToStateCache(
                  CmsOndMarketingCrossLinkingSuggestedComponent.createStateKey(context, locale),
                  () =>
                    this.locationRouteCffService.requestCityToCityLocations(
                      context.originCountry,
                      context.destinationCountry,
                      locale
                    )
                )
              )
              .pipe(
                map((suggestions) =>
                  this.toRouteGroups(suggestions, fromTo, context.currentOrigin, context.currentDestination)
                )
              );
          })
        )
        .subscribe((groups) => {
          this.groups.set(groups);
          this.loading.set(false);
          this.addRouteListSchema(groups);
        });

      onCleanup(() => subscription.unsubscribe());
    });
  }

  ngOnInit(): void {
    if (this.type() === OndCrossLinkingType.CITY_TO_CITY_LANDING) {
      this.ondMarketingSeoService.setBreadcrumb(OndMarketingRoutesPath.CITY_TO_CITY);
    }
  }

  private addRouteListSchema(groups: SuggestedRouteGroup[]): void {
    const baseUrl = this.configService.cfg.baseUrl;
    const items = groups.flatMap((group) =>
      group.links.map((link) => ({
        url: `${baseUrl}${link.href}`,
        name: `${link.originCityName}—${link.destinationCityName}`,
      }))
    );

    if (!hasNavigableLinks(groups)) {
      return;
    }

    this.schemaService.addRouteListSchema({ name: this.cityToCityHeading(), items });
  }

  private landingContext(langValue: string): SuggestedRouteContext {
    return { originCountry: langValue === 'en' ? 'fi' : getCurrentCountry(langValue) };
  }

  private cityToCityContext$(params: Params | undefined): Observable<SuggestedRouteContext | null> {
    const origin = transformLocationCode(params?.originLocationCode);
    const destination = transformLocationCode(params?.destinationLocationCode);

    if (!origin || !destination) {
      return of(null);
    }

    return this.locationsService.triggerLocationFetchAll([origin, destination]).pipe(
      map((locations) => {
        const originCountry = locations[origin]?.countryCode?.toLowerCase();
        const destinationCountry = locations[destination]?.countryCode?.toLowerCase();

        if (!originCountry || !destinationCountry) {
          return null;
        }

        return { originCountry, destinationCountry, currentOrigin: origin, currentDestination: destination };
      })
    );
  }

  private toRouteGroups(
    suggestions: LocationSuggestions,
    fromTo: string,
    currentOrigin: string | undefined,
    currentDestination: string | undefined
  ): SuggestedRouteGroup[] {
    const hasCityName = (location: Location): boolean => !!location.cityName;
    const currentOriginFirst = (a: Location, b: Location): number =>
      Number(b.locationCode === currentOrigin) - Number(a.locationCode === currentOrigin);

    const destinations = suggestions.destinations.filter(hasCityName);

    return suggestions.departures
      .filter(hasCityName)
      .sort(currentOriginFirst)
      .map((origin) => ({
        originLocationCode: origin.locationCode,
        originCityName: origin.cityName,
        links: this.linksFor(origin, destinations, currentOrigin, currentDestination, fromTo),
      }))
      .filter((group) => group.links.length > 0);
  }

  private linksFor(
    origin: Location,
    destinations: Location[],
    currentOrigin: string | undefined,
    currentDestination: string | undefined,
    fromTo: string
  ): SuggestedRouteLink[] {
    const isSameCity = (destination: Location): boolean => destination.locationCode === origin.locationCode;
    const isCurrentRoute = (destination: Location): boolean =>
      origin.locationCode === currentOrigin && destination.locationCode === currentDestination;

    return destinations
      .filter((destination) => !isSameCity(destination))
      .filter(uniqueBy((destination) => destination.locationCode))
      .map((destination) => this.toLink(origin, destination, fromTo, isCurrentRoute(destination)));
  }

  private toLink(origin: Location, destination: Location, fromTo: string, disabled: boolean): SuggestedRouteLink {
    return {
      href: createCityToCityLink(
        this.languageService.langValue,
        origin.locationCode,
        destination.locationCode,
        { fromTo } as OndLinkTranslations,
        { originCityName: origin.cityName, destinationCityName: destination.cityName } as OndPairEnhanced
      ),
      originCityName: origin.cityName,
      destinationCityName: destination.cityName,
      disabled,
    };
  }
}
